import React, { useState, useEffect } from 'react';
import {
  ScaleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

// Types
interface CountryInfo {
  code: string;
  name: string;
  regions_label: string;
}

interface RegionInfo {
  code: string;
  name: string;
}

interface ClaimTypeInfo {
  id: string;
  name: string;
  description: string;
  category: string;
  evidence_needed: string[];
}

interface StateRuleData {
  name: string;
  max_amount: number;
  max_amount_business?: number;
  filing_fee_range: { min: number; max: number };
  court_name: string;
  service_methods: string[];
  service_deadline_days?: number;
  statute_of_limitations: Record<string, number>;
  appeal_allowed?: boolean;
  appeal_deadline_days?: number;
  response_deadline_days?: number;
  forms?: string[];
  notes?: string;
}

interface StateRuleResponse {
  state: string;
  rules: StateRuleData;
}

interface EligibilityResult {
  eligible: boolean;
  reasons: string[];
  max_amount: number;
  filing_fee_estimate: { min: number; max: number };
  statute_of_limitations_ok: boolean;
  sol_deadline: string;
  days_remaining: number;
  court_name: string;
  state_name: string;
  notes: string;
  next_steps: string[];
}

interface DamagesItem {
  description: string;
  amount: number;
  category: string;
}

interface DamagesResult {
  total_damages: number;
  itemized_list: DamagesItem[];
  categories: Record<string, number>;
  notes: string[];
}

interface EvidenceItem {
  item: string;
  description: string;
  required: boolean;
}

interface EvidenceChecklist {
  claim_type: string;
  claim_name: string;
  checklist: EvidenceItem[];
  tips: string[];
}

interface FilingStep {
  step: number;
  title: string;
  description: string;
}

interface FilingInstructions {
  state: string;
  state_name: string;
  court_name: string;
  steps: FilingStep[];
  max_claim_amount?: number;
  filing_fee_range?: { min: number; max: number };
  service_methods?: string[];
  notes?: string;
}

interface DemandLetterResponse {
  letter: string;
  deadline_date: string;
  claim_type: string;
  amount: number;
  tips: string[];
}

interface ClaimFormResponse {
  form: string;
  state: string;
  state_name: string;
  court_name: string;
  filing_fee_range: { min: number; max: number };
  required_forms: string[];
  service_deadline_days: number;
  filing_checklist: string[];
}

// Step definitions
const STEPS = ['state', 'eligibility', 'damages', 'demandLetter', 'claimForm', 'evidence', 'instructions'];

export const SmallClaims: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Current step
  const [currentStep, setCurrentStep] = useState(0);

  // Location selection (country + region)
  const [countries, setCountries] = useState<CountryInfo[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState<RegionInfo[]>([]);
  const [regionsLabel, setRegionsLabel] = useState('State');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [stateRules, setStateRules] = useState<StateRuleData | null>(null);

  // Claim types
  const [claimTypes, setClaimTypes] = useState<ClaimTypeInfo[]>([]);
  const [selectedClaimType, setSelectedClaimType] = useState('');

  // Eligibility form
  const [claimAmount, setClaimAmount] = useState<number>(0);
  const [incidentDate, setIncidentDate] = useState('');
  const [isBusiness, setIsBusiness] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState<EligibilityResult | null>(null);

  // Damages
  const [damageItems, setDamageItems] = useState<DamagesItem[]>([{ description: '', amount: 0, category: 'actual' }]);
  const [damagesResult, setDamagesResult] = useState<DamagesResult | null>(null);

  // Demand Letter
  const [senderName, setSenderName] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [damagesDescription, setDamagesDescription] = useState('');
  const [previousAttempts, setPreviousAttempts] = useState('');
  const [deadlineDays, setDeadlineDays] = useState(14);
  const [demandLetter, setDemandLetter] = useState('');

  // Claim Form
  const [plaintiffPhone, setPlaintiffPhone] = useState('');
  const [plaintiffEmail, setPlaintiffEmail] = useState('');
  const [claimDescription, setClaimDescription] = useState('');
  const [claimFormResponse, setClaimFormResponse] = useState<ClaimFormResponse | null>(null);

  // Evidence
  const [evidenceChecklist, setEvidenceChecklist] = useState<EvidenceChecklist | null>(null);

  // Filing Instructions
  const [filingInstructions, setFilingInstructions] = useState<FilingInstructions | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadCountries();
    loadClaimTypes();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadRegions(selectedCountry);
      setSelectedRegion('');
      setStateRules(null);
    }
  }, [selectedCountry]);

  // Load state rules when region changes
  useEffect(() => {
    if (selectedRegion) {
      loadStateRules();
    }
  }, [selectedRegion]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/small-claims/countries', {
        params: { language: i18n.language }
      });
      setCountries(response.data.countries);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadRegions = async (country: string) => {
    try {
      const response = await apiClient.get(`/small-claims/regions/${country}`, {
        params: { language: i18n.language }
      });
      setRegions(response.data.regions);
      setRegionsLabel(response.data.regions_label || 'Region');
    } catch (err) {
      console.error('Failed to load regions:', err);
    }
  };

  const loadClaimTypes = async () => {
    try {
      const response = await apiClient.get('/small-claims/claim-types', {
        params: { language: i18n.language }
      });
      setClaimTypes(response.data.claim_types || []);
    } catch (err) {
      console.error('Failed to load claim types:', err);
    }
  };

  const loadStateRules = async () => {
    try {
      const response = await apiClient.get<StateRuleResponse>(`/small-claims/state-rules/${selectedRegion}`, {
        params: { language: i18n.language }
      });
      setStateRules(response.data.rules);
    } catch (err) {
      console.error('Failed to load state rules:', err);
    }
  };

  const handleCheckEligibility = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/small-claims/check-eligibility', {
        state: selectedRegion,
        claim_amount: claimAmount,
        claim_type: selectedClaimType,
        incident_date: incidentDate,
        is_business: isBusiness,
        language: i18n.language
      });
      setEligibilityResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.eligibility', 'Failed to check eligibility'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateDamages = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<DamagesResult>('/small-claims/calculate-damages', {
        claim_type: selectedClaimType,
        items: damageItems.filter(d => d.description && d.amount > 0),
        language: i18n.language
      });
      setDamagesResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.damages', 'Failed to calculate damages'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDemandLetter = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<DemandLetterResponse>('/small-claims/generate-demand-letter', {
        sender_name: senderName,
        sender_address: senderAddress,
        recipient_name: recipientName,
        recipient_address: recipientAddress,
        claim_type: selectedClaimType,
        claim_amount: damagesResult?.total_damages || claimAmount,
        incident_date: incidentDate,
        incident_description: incidentDescription,
        damages_description: damagesDescription,
        previous_attempts: previousAttempts || undefined,
        deadline_days: deadlineDays,
        state: selectedRegion,
        language: i18n.language
      });
      setDemandLetter(response.data.letter);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.demandLetter', 'Failed to generate demand letter'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateClaimForm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<ClaimFormResponse>('/small-claims/generate-claim-form', {
        state: selectedRegion,
        plaintiff_name: senderName,
        plaintiff_address: senderAddress,
        plaintiff_phone: plaintiffPhone,
        plaintiff_email: plaintiffEmail || undefined,
        defendant_name: recipientName,
        defendant_address: recipientAddress,
        claim_type: selectedClaimType,
        claim_amount: damagesResult?.total_damages || claimAmount,
        incident_date: incidentDate,
        claim_description: claimDescription || incidentDescription,
        damages_list: damageItems.filter(d => d.description && d.amount > 0),
        language: i18n.language
      });
      setClaimFormResponse(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.claimForm', 'Failed to generate claim form'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadEvidenceChecklist = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/small-claims/evidence-checklist/${selectedClaimType}`, {
        params: { language: i18n.language }
      });
      setEvidenceChecklist(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.evidence', 'Failed to load evidence checklist'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadFilingInstructions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/small-claims/filing-instructions/${selectedRegion}`, {
        params: { language: i18n.language }
      });
      setFilingInstructions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('smallClaims:errors.instructions', 'Failed to load filing instructions'));
    } finally {
      setIsLoading(false);
    }
  };

  const addDamageItem = () => {
    setDamageItems([...damageItems, { description: '', amount: 0, category: 'actual' }]);
  };

  const updateDamageItem = (index: number, field: keyof DamagesItem, value: string | number) => {
    const updated = [...damageItems];
    updated[index] = { ...updated[index], [field]: value };
    setDamageItems(updated);
  };

  const removeDamageItem = (index: number) => {
    setDamageItems(damageItems.filter((_, i) => i !== index));
  };

  // Export helpers for different formats
  const exportContent = async (content: string, filename: string, format: 'word' | 'pdf' | 'text') => {
    if (format === 'text') {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (format === 'word') {
      // Create simple Word document
      const htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
        <head><meta charset='utf-8'><title>${filename}</title></head>
        <body style="font-family: Arial, sans-serif; white-space: pre-wrap;">${content.replace(/\n/g, '<br>')}</body>
        </html>
      `;
      const blob = new Blob([htmlContent], { type: 'application/msword' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      // Use browser print to PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
          <head>
            <title>${filename}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; white-space: pre-wrap; line-height: 1.6; }
            </style>
          </head>
          <body>${content.replace(/\n/g, '<br>')}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const printContent = (content: string, title: string) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; white-space: pre-wrap; line-height: 1.6; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>${content.replace(/\n/g, '<br>')}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleDemandLetterExport = (format: 'word' | 'pdf' | 'text') => {
    if (demandLetter) {
      exportContent(demandLetter, 'demand-letter', format);
    }
  };

  const handleDemandLetterPrint = () => {
    if (demandLetter) {
      printContent(demandLetter, t('smallClaims:demandLetter.preview', 'Demand Letter'));
    }
  };

  const handleClaimFormExport = (format: 'word' | 'pdf' | 'text') => {
    if (claimFormResponse?.form) {
      exportContent(claimFormResponse.form, 'claim-form', format);
    }
  };

  const handleClaimFormPrint = () => {
    if (claimFormResponse?.form) {
      printContent(claimFormResponse.form, t('smallClaims:claimForm.preview', 'Claim Form'));
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // State selection
        return selectedRegion && selectedClaimType;
      case 1: // Eligibility
        return eligibilityResult?.eligible;
      case 2: // Damages
        // Check if total damages are within state limit
        return damagesResult && stateRules && damagesResult.total_damages <= stateRules.max_amount;
      case 3: // Demand letter
        return demandLetter;
      case 4: // Claim form
        return claimFormResponse;
      case 5: // Evidence
        return evidenceChecklist;
      default:
        return true;
    }
  };

  const goToNextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepIndicator = () => {
    const stepLabels = [
      t('smallClaims:steps.state', 'State'),
      t('smallClaims:steps.eligibility', 'Eligibility'),
      t('smallClaims:steps.damages', 'Damages'),
      t('smallClaims:steps.demandLetter', 'Demand Letter'),
      t('smallClaims:steps.claimForm', 'Claim Form'),
      t('smallClaims:steps.evidence', 'Evidence'),
      t('smallClaims:steps.instructions', 'Instructions'),
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {stepLabels.map((label, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                      ? 'bg-blue-600 dark:bg-blue-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="mt-2 text-xs text-gray-600 dark:text-gray-400 hidden sm:block">
                  {label}
                </span>
              </div>
              {index < stepLabels.length - 1 && (
                <div
                  className={`w-8 sm:w-16 h-1 mx-1 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderStateSelection = () => (
    <div className="space-y-6">
      {/* Country Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:location.selectCountry', 'Select Your Country')}
        </label>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('smallClaims:location.selectCountryPrompt', 'Select a country...')}</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>{country.name}</option>
          ))}
        </select>
      </div>

      {/* Region Selection (only shown after country is selected) */}
      {selectedCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:location.selectRegion', `Select Your ${regionsLabel}`)}
          </label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('smallClaims:location.selectRegionPrompt', `Select a ${regionsLabel.toLowerCase()}...`)}</option>
            {regions.map((region) => (
              <option key={region.code} value={region.code}>{region.name}</option>
            ))}
          </select>
        </div>
      )}

      {stateRules && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
            {t('smallClaims:state.rulesTitle', 'Small Claims Court Rules for')} {regions.find(r => r.code === selectedRegion)?.name}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('smallClaims:state.maxAmount', 'Maximum Claim')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">${stateRules.max_amount.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('smallClaims:state.filingFee', 'Filing Fee')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">${stateRules.filing_fee_range.min} - ${stateRules.filing_fee_range.max}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('smallClaims:state.statute', 'Statute of Limitations')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{stateRules.statute_of_limitations.written_contract || 4} {t('smallClaims:common.years', 'years')}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('smallClaims:state.appealsAllowed', 'Appeals Allowed')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{stateRules.appeal_allowed ? t('smallClaims:common.yes', 'Yes') : t('smallClaims:common.no', 'No')}</span>
            </div>
          </div>
          {stateRules.notes && (
            <p className="mt-3 text-sm text-blue-800 dark:text-blue-200">{stateRules.notes}</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:state.claimType', 'Type of Claim')}
        </label>
        <select
          value={selectedClaimType}
          onChange={(e) => setSelectedClaimType(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('smallClaims:state.claimTypePrompt', 'Select claim type...')}</option>
          {claimTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>{ct.name}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderEligibilityCheck = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:eligibility.claimAmount', 'Estimated Claim Amount ($)')}
          </label>
          <input
            type="number"
            value={claimAmount || ''}
            onChange={(e) => setClaimAmount(parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:eligibility.incidentDate', 'Date of Incident')}
          </label>
          <input
            type="date"
            value={incidentDate}
            onChange={(e) => setIncidentDate(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isBusiness"
          checked={isBusiness}
          onChange={(e) => setIsBusiness(e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
        />
        <label htmlFor="isBusiness" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
          {t('smallClaims:eligibility.isBusiness', 'I am filing on behalf of a business')}
        </label>
      </div>

      <button
        onClick={handleCheckEligibility}
        disabled={isLoading || !claimAmount || !incidentDate}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('smallClaims:common.checking', 'Checking...') : t('smallClaims:eligibility.checkButton', 'Check Eligibility')}
      </button>

      {eligibilityResult && (
        <div className={`p-4 rounded-lg border ${
          eligibilityResult.eligible
            ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center mb-3">
            {eligibilityResult.eligible ? (
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-2" />
            )}
            <span className={`font-medium ${
              eligibilityResult.eligible ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
            }`}>
              {eligibilityResult.eligible
                ? t('smallClaims:eligibility.eligible', 'Your claim appears eligible for small claims court')
                : t('smallClaims:eligibility.notEligible', 'Your claim may not be eligible for small claims court')}
            </span>
          </div>

          {/* Eligibility Details */}
          <div className="mb-3 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('smallClaims:eligibility.court', 'Court')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">{eligibilityResult.court_name}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('smallClaims:eligibility.maxAmount', 'Max Claim')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">${eligibilityResult.max_amount.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('smallClaims:eligibility.filingFee', 'Filing Fee')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">${eligibilityResult.filing_fee_estimate.min} - ${eligibilityResult.filing_fee_estimate.max}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('smallClaims:eligibility.deadline', 'SOL Deadline')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">{eligibilityResult.sol_deadline} ({eligibilityResult.days_remaining} {t('smallClaims:common.days', 'days')})</span>
            </div>
          </div>

          {/* Reasons */}
          {eligibilityResult.reasons && eligibilityResult.reasons.length > 0 && (
            <div className="mb-3">
              <h4 className={`font-medium mb-1 ${eligibilityResult.eligible ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                {eligibilityResult.eligible ? t('smallClaims:eligibility.reasons', 'Reasons') : t('smallClaims:eligibility.issues', 'Issues')}:
              </h4>
              <ul className={`list-disc list-inside text-sm ${eligibilityResult.eligible ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {eligibilityResult.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Next Steps */}
          {eligibilityResult.next_steps && eligibilityResult.next_steps.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('smallClaims:eligibility.nextSteps', 'Next Steps')}:</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {eligibilityResult.next_steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Notes */}
          {eligibilityResult.notes && (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic">{eligibilityResult.notes}</p>
          )}
        </div>
      )}
    </div>
  );

  const renderDamagesCalculation = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900 dark:text-white">
            {t('smallClaims:damages.itemize', 'Itemize Your Damages')}
          </h3>
          <button
            onClick={addDamageItem}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            + {t('smallClaims:damages.addItem', 'Add Item')}
          </button>
        </div>

        <div className="space-y-4">
          {damageItems.map((item, index) => (
            <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex-1">
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateDamageItem(index, 'description', e.target.value)}
                  placeholder={t('smallClaims:damages.descriptionPlaceholder', 'Description of damage/loss')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="w-32">
                <input
                  type="number"
                  value={item.amount || ''}
                  onChange={(e) => updateDamageItem(index, 'amount', parseFloat(e.target.value) || 0)}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="w-36">
                <select
                  value={item.category}
                  onChange={(e) => updateDamageItem(index, 'category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="actual">{t('smallClaims:damages.categories.actual', 'Actual')}</option>
                  <option value="consequential">{t('smallClaims:damages.categories.consequential', 'Consequential')}</option>
                  <option value="incidental">{t('smallClaims:damages.categories.incidental', 'Incidental')}</option>
                </select>
              </div>
              {damageItems.length > 1 && (
                <button
                  onClick={() => removeDamageItem(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleCalculateDamages}
        disabled={isLoading || !damageItems.some(d => d.description && d.amount > 0)}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('smallClaims:common.calculating', 'Calculating...') : t('smallClaims:damages.calculateButton', 'Calculate Total Damages')}
      </button>

      {damagesResult && stateRules && (
        <div className={`p-4 rounded-lg border ${
          damagesResult.total_damages <= stateRules.max_amount
            ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium text-gray-900 dark:text-white">
              {t('smallClaims:damages.totalDamages', 'Total Damages')}:
            </span>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              ${damagesResult.total_damages.toLocaleString()}
            </span>
          </div>

          <div className="text-sm">
            <span className={damagesResult.total_damages <= stateRules.max_amount ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300'}>
              {damagesResult.total_damages <= stateRules.max_amount
                ? t('smallClaims:damages.withinLimit', 'Within small claims limit')
                : t('smallClaims:damages.exceedsLimit', 'Exceeds small claims limit of ${{limit}}', { limit: stateRules.max_amount.toLocaleString() })}
            </span>
          </div>

          {/* Category breakdown */}
          {Object.keys(damagesResult.categories).length > 0 && (
            <div className="mt-3">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('smallClaims:damages.breakdown', 'By Category')}:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(damagesResult.categories).map(([category, amount]) => (
                  <div key={category} className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400 capitalize">{category}:</span>
                    <span className="text-gray-900 dark:text-white">${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {damagesResult.notes && damagesResult.notes.length > 0 && (
            <div className="mt-3">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('smallClaims:damages.notes', 'Notes')}:</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {damagesResult.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderDemandLetter = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('smallClaims:demandLetter.description', 'Before filing in small claims court, you should send a formal demand letter to the other party. This shows good faith effort to resolve the dispute.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:demandLetter.senderName', 'Your Name')}
          </label>
          <input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:demandLetter.recipientName', 'Recipient Name')}
          </label>
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:demandLetter.senderAddress', 'Your Address')}
          </label>
          <textarea
            value={senderAddress}
            onChange={(e) => setSenderAddress(e.target.value)}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:demandLetter.recipientAddress', 'Recipient Address')}
          </label>
          <textarea
            value={recipientAddress}
            onChange={(e) => setRecipientAddress(e.target.value)}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:demandLetter.incidentDescription', 'Description of Incident')}
        </label>
        <textarea
          value={incidentDescription}
          onChange={(e) => setIncidentDescription(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('smallClaims:demandLetter.incidentPlaceholder', 'Describe what happened...')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:demandLetter.damagesDescription', 'Description of Damages')}
        </label>
        <textarea
          value={damagesDescription}
          onChange={(e) => setDamagesDescription(e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('smallClaims:demandLetter.damagesPlaceholder', 'Describe the damages you suffered...')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:demandLetter.previousAttempts', 'Previous Resolution Attempts (Optional)')}
        </label>
        <textarea
          value={previousAttempts}
          onChange={(e) => setPreviousAttempts(e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('smallClaims:demandLetter.previousPlaceholder', 'Any previous attempts to resolve this issue...')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:demandLetter.deadlineDays', 'Response Deadline (Days)')}
        </label>
        <input
          type="number"
          value={deadlineDays}
          onChange={(e) => setDeadlineDays(parseInt(e.target.value) || 14)}
          className="w-32 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <button
        onClick={handleGenerateDemandLetter}
        disabled={isLoading || !senderName || !recipientName || !incidentDescription}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('smallClaims:common.generating', 'Generating...') : t('smallClaims:demandLetter.generateButton', 'Generate Demand Letter')}
      </button>

      {demandLetter && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t('smallClaims:demandLetter.preview', 'Demand Letter Preview')}
            </h3>
            <ExportDropdownMenu
              onExport={handleDemandLetterExport}
              onPrint={handleDemandLetterPrint}
              showPrint={true}
            />
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
              {demandLetter}
            </pre>
          </div>
        </div>
      )}
    </div>
  );

  const renderClaimForm = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('smallClaims:claimForm.description', 'Generate a draft small claims court complaint form with all required information.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:claimForm.phone', 'Phone Number')}
          </label>
          <input
            type="tel"
            value={plaintiffPhone}
            onChange={(e) => setPlaintiffPhone(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('smallClaims:claimForm.email', 'Email (Optional)')}
          </label>
          <input
            type="email"
            value={plaintiffEmail}
            onChange={(e) => setPlaintiffEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('smallClaims:claimForm.claimDescription', 'Detailed Claim Description')}
        </label>
        <textarea
          value={claimDescription}
          onChange={(e) => setClaimDescription(e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('smallClaims:claimForm.claimPlaceholder', 'Provide a detailed description of your claim for the court...')}
        />
      </div>

      <button
        onClick={handleGenerateClaimForm}
        disabled={isLoading || !plaintiffPhone || !senderName}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('smallClaims:common.generating', 'Generating...') : t('smallClaims:claimForm.generateButton', 'Generate Claim Form')}
      </button>

      {claimFormResponse && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t('smallClaims:claimForm.preview', 'Claim Form Preview')}
            </h3>
            <ExportDropdownMenu
              onExport={handleClaimFormExport}
              onPrint={handleClaimFormPrint}
              showPrint={true}
            />
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:claimForm.court', 'Court')}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{claimFormResponse.court_name}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:claimForm.state', 'State')}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{claimFormResponse.state_name}</span>
                </div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:claimForm.filingFee', 'Filing Fee')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">${claimFormResponse.filing_fee_range.min} - ${claimFormResponse.filing_fee_range.max}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:claimForm.requiredForms', 'Required Forms')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{claimFormResponse.required_forms.join(', ')}</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono overflow-auto max-h-96">
              {claimFormResponse.form}
            </pre>
          </div>

          {claimFormResponse.filing_checklist && claimFormResponse.filing_checklist.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('smallClaims:claimForm.filingChecklist', 'Filing Checklist')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200">
                {claimFormResponse.filing_checklist.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderEvidenceChecklist = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('smallClaims:evidence.description', 'Review the evidence checklist for your type of claim to ensure you have everything you need.')}
      </p>

      {!evidenceChecklist && (
        <button
          onClick={handleLoadEvidenceChecklist}
          disabled={isLoading}
          className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('smallClaims:common.loading', 'Loading...') : t('smallClaims:evidence.loadButton', 'Load Evidence Checklist')}
        </button>
      )}

      {evidenceChecklist && (
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-1">
              {evidenceChecklist.claim_name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('smallClaims:evidence.checklistFor', 'Evidence checklist for your claim type')}
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('smallClaims:evidence.checklist', 'Evidence Checklist')}
            </h3>
            <div className="space-y-2">
              {evidenceChecklist.checklist.map((item, i) => (
                <div key={i} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{item.item.replace(/_/g, ' ')}</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      item.required
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                    }`}>
                      {item.required ? t('smallClaims:evidence.required', 'Required') : t('smallClaims:evidence.optional', 'Optional')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {evidenceChecklist.tips && evidenceChecklist.tips.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('smallClaims:evidence.tips', 'Tips')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200">
                {evidenceChecklist.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderFilingInstructions = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('smallClaims:instructions.description', 'Review the filing instructions for small claims court in your state.')}
      </p>

      {!filingInstructions && (
        <button
          onClick={handleLoadFilingInstructions}
          disabled={isLoading}
          className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('smallClaims:common.loading', 'Loading...') : t('smallClaims:instructions.loadButton', 'Load Filing Instructions')}
        </button>
      )}

      {filingInstructions && (
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('smallClaims:instructions.overview', 'Overview')}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:instructions.state', 'State')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{filingInstructions.state_name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('smallClaims:instructions.courtName', 'Court')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{filingInstructions.court_name}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('smallClaims:instructions.steps', 'Filing Steps')}
            </h3>
            <div className="space-y-3">
              {filingInstructions.steps?.map((step: FilingStep) => (
                <div key={step.step} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-sm font-medium">
                    {step.step}
                  </span>
                  <div className="ml-3">
                    <span className="font-medium text-gray-900 dark:text-white">{step.title}</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {filingInstructions.notes && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                {t('smallClaims:instructions.notes', 'Important Notes')}
              </h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{filingInstructions.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStateSelection();
      case 1:
        return renderEligibilityCheck();
      case 2:
        return renderDamagesCalculation();
      case 3:
        return renderDemandLetter();
      case 4:
        return renderClaimForm();
      case 5:
        return renderEvidenceChecklist();
      case 6:
        return renderFilingInstructions();
      default:
        return null;
    }
  };

  const getStepIcon = () => {
    const icons = [
      <ScaleIcon className="h-6 w-6" />,
      <CheckCircleIcon className="h-6 w-6" />,
      <CurrencyDollarIcon className="h-6 w-6" />,
      <EnvelopeIcon className="h-6 w-6" />,
      <DocumentDuplicateIcon className="h-6 w-6" />,
      <ClipboardDocumentListIcon className="h-6 w-6" />,
      <DocumentTextIcon className="h-6 w-6" />,
    ];
    return icons[currentStep];
  };

  const getStepTitle = () => {
    const titles = [
      t('smallClaims:stepTitles.state', 'Select Your State & Claim Type'),
      t('smallClaims:stepTitles.eligibility', 'Check Eligibility'),
      t('smallClaims:stepTitles.damages', 'Calculate Damages'),
      t('smallClaims:stepTitles.demandLetter', 'Generate Demand Letter'),
      t('smallClaims:stepTitles.claimForm', 'Generate Claim Form'),
      t('smallClaims:stepTitles.evidence', 'Evidence Checklist'),
      t('smallClaims:stepTitles.instructions', 'Filing Instructions'),
    ];
    return titles[currentStep];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/scales-logo.png"
            alt="Scales of Justice"
            className="h-24 w-24 mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('smallClaims:title', 'Small Claims Court Assistant')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('smallClaims:subtitle', 'Navigate the small claims court process step by step')}
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              {t('smallClaims:common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-600 dark:text-blue-400 mr-4">
              {getStepIcon()}
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {getStepTitle()}
            </h2>
          </div>

          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={goToPreviousStep}
            disabled={currentStep === 0}
            className="flex items-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('smallClaims:navigation.previous', 'Previous')}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={goToNextStep}
              disabled={!canProceed()}
              className="flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('smallClaims:navigation.next', 'Next')}
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(0)}
              className="flex items-center px-6 py-3 bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('smallClaims:navigation.done', 'Done')}
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">{t('smallClaims:disclaimer.title', 'Important Disclaimer')}</p>
              <p>
                {t('smallClaims:disclaimer.text', 'This tool provides general information about small claims court procedures and generates draft documents for educational purposes. It does not constitute legal advice. Laws and procedures vary by jurisdiction and may change. Always verify information with your local court and consider consulting with a licensed attorney.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
